import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useRef, type ReactNode } from 'react';

import { BUTTON_SIZE, BUTTON_VARIANT } from './types/button-variants-enums';
import { Button } from '@/components/ui/button';
import { DIALOG_DISMISSAL, DIALOG_SIZE } from './types/dialog-variants-enums';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { CONFIRMATION_DIALOG_TONE } from './types/confirmation-dialog-variants-enums';



export interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  tone?: CONFIRMATION_DIALOG_TONE;
  confirmDisabled?: boolean;
  hideCancel?: boolean;
  busy?: boolean;
  errorMessage?: string;
  className?: string;
}

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  tone = CONFIRMATION_DIALOG_TONE.DEFAULT,
  confirmDisabled = false,
  hideCancel = false,
  busy = false,
  errorMessage,
  className,
}: ConfirmationDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const isDestructive = tone === CONFIRMATION_DIALOG_TONE.DESTRUCTIVE;
  const focusCancelOnOpen = isDestructive && !hideCancel;

  const handleOpenChange = (nextOpen: boolean) => {
    if (busy && !nextOpen) {
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleCancel = () => {
    if (busy) {
      return;
    }
    onCancel?.();
    onOpenChange(false);
  };

  const confirmAndClose = async () => {
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error('Confirmation action failed', { error });
    }
  };

  const handleConfirm = () => {
    if (busy || confirmDisabled) {
      return;
    }

    void confirmAndClose();
  };

  const confirmVariant =
    tone === CONFIRMATION_DIALOG_TONE.DESTRUCTIVE
      ? BUTTON_VARIANT.DESTRUCTIVE
      : BUTTON_VARIANT.PRIMARY;

  const confirmToneClass =
    tone === CONFIRMATION_DIALOG_TONE.WARNING
      ? 'bg-status-warning-solid text-status-warning-solid-foreground hover:bg-status-warning-foreground'
      : undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        role="alertdialog"
        size={DIALOG_SIZE.SM}
        dismissal={busy ? DIALOG_DISMISSAL.BLOCKED : DIALOG_DISMISSAL.STANDARD}
        busy={busy}
        hideClose
        data-slot="confirmation-dialog"
        data-tone={tone}
        className={cn(className)}
        onOpenAutoFocus={event => {
          if (!focusCancelOnOpen) {
            return;
          }
          event.preventDefault();
          cancelRef.current?.focus();
        }}
        onPointerDownOutside={event => {
          event.preventDefault();
        }}
        onInteractOutside={event => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <VisuallyHidden asChild>
              <DialogDescription>{title}</DialogDescription>
            </VisuallyHidden>
          )}
        </DialogHeader>
        {children ? (
          <div className="py-2 text-sm text-content-secondary">{children}</div>
        ) : null}
        {errorMessage ? (
          <div
            role="alert"
            data-slot="confirmation-dialog-error"
            className="text-sm text-status-danger-foreground"
          >
            {errorMessage}
          </div>
        ) : null}
        <DialogFooter>
          {hideCancel ? null : (
            <Button
              ref={cancelRef}
              type="button"
              size={BUTTON_SIZE.DEFAULT}
              variant={BUTTON_VARIANT.SECONDARY}
              disabled={busy}
              onClick={handleCancel}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            type="button"
            size={BUTTON_SIZE.DEFAULT}
            variant={confirmVariant}
            disabled={confirmDisabled || busy}
            className={cn(confirmToneClass)}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
